'use client';

import { getSupabaseClient } from '@popow/infra/lib/supabase/client';
import { useRouter, useSearchParams } from 'next/navigation';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { usePageViewTracking } from '@/features/common/analytics/hooks';
import {
  createPostAuthNextCookie,
  deletePostAuthNextCookie,
} from '@/features/common/config/authCookies';
import { SUPPORT_EMAIL } from '@/features/common/config/contact';
import { HOME_PATH } from '@/features/common/config/routePaths';
import { useAttribution } from '@/features/common/hooks/useAttribution';
import Icon from '@/features/common/ui/atoms/Icon';
import PageContainer from '@/features/common/ui/atoms/PageContainer';
import { CommonModal } from '@/features/common/ui/CommonModal';
import { dbLogger } from '@/features/common/utils/dbLogger';
import { isValidRedirect } from '@/features/common/utils/isValidRedirect';
import { TikTokHandleValidator } from '@/features/profile/domain/TikTokHandleValidator';
import { useAuth } from '@/shared/auth';
import EmailLoginForm from './components/EmailLoginForm';

/**
 * Social proof 텍스트 내의 특정 키워드를 강조 표시하는 헬퍼 함수
 */
function renderSocialProof(text: string): React.ReactNode {
  // 마침표를 "millions of views"에 붙여서 처리
  const normalizedText = text.replace(/(millions of views)\s*\./, '$1.');
  const parts = normalizedText.split(/(5,000\+ creators|millions of views\.?)/);

  return parts.map((part, index) => {
    if (/5,000\+ creators|millions of views\.?/.test(part)) {
      return (
        <span key={index} className="font-semibold text-semantic-label-strong">
          {part}
        </span>
      );
    }
    return part;
  });
}

export default function LoginPage() {
  const user = useAuth(s => s.user);
  const isAuthLoading = useAuth(s => s.isLoading);
  // Removed authService dependency
  const router = useRouter();
  const searchParams = useSearchParams();
  const { attributionParams } = useAttribution();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [hashProcessing, setHashProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const nextUrl = searchParams.get('next');
  const safeNextUrl = nextUrl && isValidRedirect(nextUrl) ? nextUrl : undefined;

  // 로그인 모드: 기본은 TikTok, ?admin=1이면 이메일 로그인
  const isAdminMode = searchParams.get('admin') === 'true';
  const isTikTokEnabled = !isAdminMode;

  // [MIGRATION] TikTok 연동 필수 안내 (middleware에서 리다이렉트된 경우)
  // TODO: 마이그레이션 완료 후 이 변수들과 관련 UI 제거
  const reason = searchParams.get('reason');
  const isTikTokRequired = reason === 'tiktok_required';

  // 프로필 권한 거부 모달 상태
  const [showProfileScopeModal, setShowProfileScopeModal] = useState(false);

  // URL 에러 처리 (hash 토큰이 있을 때는 invalid_callback 무시)
  useEffect(() => {
    const errorParam = searchParams.get('error');
    const hash = typeof window !== 'undefined' ? window.location.hash : '';
    const hasHashTokens = hash.includes('access_token');

    const isProfileError =
      errorParam === 'profile_scope_missing' ||
      errorParam === 'tiktok_access_denied' ||
      errorParam === 'access_denied'; // TikTok 로그인 취소

    // Case 2-B: TikTok 계정 불일치 에러 (보안)
    const isTikTokMismatchError = errorParam === 'tiktok_account_mismatch';
    // TikTok OAuth 없이 직접 접근 시도
    const isTikTokRequiredError = errorParam === 'tiktok_required';

    if (isTikTokMismatchError) {
      setError(
        `This email is already linked to a different TikTok account. Please contact support at ${SUPPORT_EMAIL}`,
      );
    } else if (isTikTokRequiredError) {
      setError('Please sign in with TikTok to continue.');
    } else if (isProfileError) {
      setShowProfileScopeModal(true);
    } else if (
      nextUrl &&
      (nextUrl.includes('error=profile_scope_missing') ||
        nextUrl.includes('error=tiktok_access_denied') ||
        nextUrl.includes('error=access_denied'))
    ) {
      // 미들웨어를 거쳐서 온 경우 체크
      setShowProfileScopeModal(true);
    } else if (errorParam) {
      // hash 토큰이 있으면 invalid_callback은 숨김
      if (errorParam === 'invalid_callback' && hasHashTokens) {
        return;
      }
      setError(errorParam);
    }
  }, [searchParams, nextUrl]);

  // Track page view once on mount
  const rawHandle =
    searchParams.get('tiktok_handle') || attributionParams?.get('tiktok_handle') || null;
  const validation = rawHandle ? TikTokHandleValidator.validate(rawHandle) : { valid: false };
  const isPersonalized = validation.valid;
  const normalizedHandle = validation.normalizedHandle;

  // Track page view once on mount
  usePageViewTracking('View Login', {
    properties: {
      is_personalized: isPersonalized,
      ...(isPersonalized ? { tiktok_handle: normalizedHandle } : {}),
    },
    deps: [isPersonalized, normalizedHandle],
  });

  // Handle Supabase magic link hash fragments (#access_token=...&refresh_token=...)
  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }
    const hash = window.location.hash;
    if (!hash || !hash.includes('access_token')) {
      return;
    }

    // 해시 토큰이 있으면 버튼 비활성화
    setHashProcessing(true);

    const params = new URLSearchParams(hash.replace(/^#/, ''));
    const accessToken = params.get('access_token');
    const refreshToken = params.get('refresh_token');
    const next = params.get('next');

    if (!accessToken || !refreshToken) {
      return;
    }

    // hash로 왔을 때 기존 error 쿼리 제거
    if (window.location.search.includes('error=')) {
      const url = new URL(window.location.href);
      url.searchParams.delete('error');
      window.history.replaceState(null, '', url.toString());
    }

    const supabase = getSupabaseClient();
    const redirectCandidate = next || safeNextUrl;
    const targetUrl =
      redirectCandidate && isValidRedirect(redirectCandidate) ? redirectCandidate : HOME_PATH;

    supabase.auth
      .setSession({
        access_token: accessToken,
        refresh_token: refreshToken,
      })
      .then(({ error: sessionError }) => {
        if (sessionError) {
          dbLogger.error('auth', 'Failed to set session from hash', {
            error: sessionError.message,
          });
          setError('invalid_callback');
          return;
        }
        // Clean hash and redirect
        window.history.replaceState(null, '', targetUrl);
        router.replace(targetUrl);
      })
      .catch(e => {
        dbLogger.error('auth', 'Unexpected error while setting session from hash', {
          error: e instanceof Error ? e.message : String(e),
        });
        setError('invalid_callback');
      });
  }, [router, safeNextUrl]);

  useEffect(() => {
    if (!isAuthLoading && user) {
      // Always replace to avoid login page in history
      // Use window.history.replaceState for additional safety
      const targetUrl = safeNextUrl || HOME_PATH;
      if (typeof window !== 'undefined') {
        window.history.replaceState(null, '', targetUrl);
      }
      router.replace(targetUrl);
    }
  }, [user, isAuthLoading, router, safeNextUrl]);

  /* Removed handleTikTokSignin definition inside component */

  const handleTikTokSignin = async () => {
    // Moved import to top-level in real file, but here showing usage
    // Using the utility function instead of service
    setLoading(true);
    setError(null);
    try {
      if (safeNextUrl) {
        try {
          // biome-ignore lint/suspicious/noDocumentCookie: Required for auth redirect
          document.cookie = createPostAuthNextCookie(safeNextUrl);
        } catch {
          // 쿠키 설정 실패는 플로우에 영향 없음 (조용히 무시)
        }
      }

      // Use imported utility
      // Dynamic import to avoid hydration issues if it uses window (it guards inside but good practice)
      const { signInWithTikTok } = await import('@/features/tiktok');
      await signInWithTikTok({ redirectTo: safeNextUrl });
    } catch (e: unknown) {
      try {
        // biome-ignore lint/suspicious/noDocumentCookie: Required for auth redirect cleanup
        document.cookie = deletePostAuthNextCookie();
      } catch {
        // 쿠키 삭제 실패는 플로우에 영향 없음 (조용히 무시)
      }
      const errorMessage = e instanceof Error ? e.message : 'Failed to start TikTok sign-in';
      setError(errorMessage);
      setLoading(false);
    }
  };

  return (
    <PageContainer className="flex flex-col">
      <div className="mx-auto w-full max-w-md flex-1 px-5 pt-10 text-center">
        <div className="mb-6 flex justify-center">
          <button
            onClick={() => window.open('https://popow.ai', '_blank')}
            className="cursor-pointer"
          >
            <Icon name="popow-logo-green" width={109} height={38} alt="Popow logo" />
          </button>
        </div>

        {/* [MIGRATION] TikTok 연동 필수 안내 배너 (middleware에서 리다이렉트된 경우) */}
        {/* TODO: 마이그레이션 완료 후 이 블록 제거 */}
        {isTikTokRequired && (
          <div className="mb-4 rounded-lg border border-semantic-status-cautionary/22 bg-semantic-status-cautionary/8 p-4 text-center">
            <p className="font-mona-sans text-body-1-normal-semibold text-semantic-status-cautionary">
              ⚠️ TikTok Connection Incomplete
            </p>
            <p className="mt-1 font-mona-sans text-caption-1-regular text-semantic-label-normal">
              Something went wrong. Please try signing in again.
            </p>
          </div>
        )}

        {/* Personalized headline or regular headline */}
        {isPersonalized ? (
          <h1 className="mt-4 whitespace-pre-line text-center font-mona-sans text-semantic-label-strong text-title-2-bold">
            Hey <span className="text-semantic-primary-normal">@{normalizedHandle}</span>,{'\n'}
            {t('Login.personalizedHeadline')}
          </h1>
        ) : (
          <h1 className="mt-4 whitespace-pre-line text-center font-mona-sans text-semantic-label-strong text-title-2-bold">
            {t('Login.headline')}
          </h1>
        )}
        <p className="mt-2 text-center font-mona-sans text-body-1-normal-regular text-semantic-label-normal">
          {t('Login.subline')}
        </p>
        <div className="mt-1 text-center font-mona-sans text-body-1-normal-regular text-semantic-label-neutral">
          {renderSocialProof(t('Login.socialProof'))}
        </div>

        {/* 틱톡 로그인 (환경변수로 제어) - true일 때 TikTok만 표시 */}
        {isTikTokEnabled && (
          <>
            <div className="mt-8 flex justify-center">
              <div className="relative">
                <Button
                  type="button"
                  variant="cta-outlined-assistive"
                  fullWidth={false}
                  onClick={handleTikTokSignin}
                  disabled={loading || hashProcessing}
                  isLoading={loading || hashProcessing}
                  data-testid="tiktok-login-button"
                  className="group min-w-[240px] border-atomic-cool-neutral-500/[0.22] transition-all hover:border-atomic-black hover:bg-atomic-black hover:text-white"
                  leftIcon={
                    <Icon
                      name="tiktok"
                      width={20}
                      height={20}
                      alt="TikTok"
                      className="transition-all group-hover:brightness-0 group-hover:invert"
                    />
                  }
                >
                  Continue with TikTok
                </Button>
              </div>
            </div>
            {error && (
              <div className="mt-3 rounded-lg bg-semantic-status-destructive/10 p-3 text-center font-mona-sans text-label-1-normal-regular text-semantic-status-destructive">
                {error}
              </div>
            )}
          </>
        )}

        {/* 이메일 로그인 폼 (환경변수로 제어) - false일 때 이메일만 표시 */}
        {!isTikTokEnabled && (
          <div className="mt-8">
            <EmailLoginForm redirectUrl={safeNextUrl} />
          </div>
        )}
      </div>

      {/* Terms and Privacy Policy Links */}
      <div className="px-5 pb-10">
        <p className="text-center font-mona-sans text-caption-1-regular text-semantic-label-assistive">
          By continuing, you agree to our{' '}
          <a
            href="https://popow.ai/terms"
            target="_blank"
            rel="noopener noreferrer"
            className="underline hover:text-semantic-label-normal"
          >
            Terms of Service
          </a>{' '}
          and{' '}
          <a
            href="https://popow.ai/privacy"
            target="_blank"
            rel="noopener noreferrer"
            className="underline hover:text-semantic-label-normal"
          >
            Privacy Policy
          </a>
        </p>
      </div>

      {/* 프로필 권한 거부 모달 (틱톡 활성화 시만 표시) */}
      {isTikTokEnabled && (
        <CommonModal
          isOpen={showProfileScopeModal}
          onClose={() => {
            // 모달 닫기 불가 처리 - 닫으려고 하면 다시 열기
            setShowProfileScopeModal(true);
          }}
          title="Profile Permission Required"
          description={[
            'To continue, we need access to your TikTok profile information.',
            'Please reconnect your TikTok account and grant profile permissions.',
          ]}
          primaryActionText="Reconnect TikTok"
          onPrimaryAction={() => {
            setShowProfileScopeModal(false);
            // URL에서 에러 제거 (재시도 시 모달이 즉시 다시 뜨는 것 방지)
            if (typeof window !== 'undefined') {
              const newUrl = new URL(window.location.href);
              newUrl.searchParams.delete('error');
              window.history.replaceState({}, '', newUrl.toString());
            }
            handleTikTokSignin();
          }}
          // Secondary 액션 없음 (모달 닫기 불가)
        />
      )}
    </PageContainer>
  );
}
